import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getUserGymId } from "@/utils/gymAuth";
import { getCurrentUserIdSync } from "@/utils/authHelpers";
import { Loader2 } from "lucide-react";
import { getCourses } from "@/services/edgeFunctionWrappers/coursesManagementWrapper";
import { updateUserGymAssignments } from "@/services/edgeFunctionWrappers/userManagementWrapper";

interface Course {
  id: string;
  name: string;
  category_id?: string;
}

interface AllowedCoursesSelectorProps {
  userId: string;
  currentAllowedCourses: string[] | null;
  onUpdate?: () => void;
}

export function AllowedCoursesSelector({ 
  userId, 
  currentAllowedCourses,
  onUpdate 
}: AllowedCoursesSelectorProps) {
  const { t } = useTranslation("adminui3");
  const [courses, setCourses] = useState<Course[]>([]);
  const [isAllCoursesEnabled, setIsAllCoursesEnabled] = useState(currentAllowedCourses === null);
  const [selectedCourses, setSelectedCourses] = useState<string[]>(currentAllowedCourses || []);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    setIsAllCoursesEnabled(currentAllowedCourses === null);
    setSelectedCourses(currentAllowedCourses || []);
  }, [currentAllowedCourses]);

  const fetchCourses = async () => {
    setIsLoading(true);
    try {
      const gymId = getUserGymId();
      const courses = await getCourses(gymId);
      setCourses(courses || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error(t('allowedCourses.fetchError', 'Erreur lors du chargement des cours'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAllCoursesChange = (checked: boolean) => {
    setIsAllCoursesEnabled(checked);
    if (checked) {
      setSelectedCourses([]);
    }
  };

  const handleCourseToggle = (courseId: string, checked: boolean) => {
    if (checked) {
      setSelectedCourses(prev => [...prev, courseId]);
    } else {
      setSelectedCourses(prev => prev.filter(id => id !== courseId));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const gymId = getUserGymId();
      if (!gymId) {
        throw new Error('No gym selected');
      }

      // Use sync helper to get current user ID from store
      const currentUserId = getCurrentUserIdSync();
      if (!currentUserId) {
        throw new Error('No authenticated user');
      }

      const allowedCoursesValue = isAllCoursesEnabled ? null : selectedCourses;
      
      await updateUserGymAssignments(currentUserId, gymId, userId, { allowed_courses: allowedCoursesValue });

      toast.success(t('allowedCourses.saveSuccess', 'Cours autorisés mis à jour'));
      onUpdate?.();
    } catch (error) {
      console.error('Error updating allowed courses:', error);
      toast.error(t('allowedCourses.saveError', 'Erreur lors de la sauvegarde'));
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = () => {
    const wasAllEnabled = currentAllowedCourses === null;
    if (isAllCoursesEnabled !== wasAllEnabled) return true;
    
    if (!isAllCoursesEnabled) {
      const current = currentAllowedCourses || [];
      if (current.length !== selectedCourses.length) return true;
      return !current.every(id => selectedCourses.includes(id));
    }
    
    return false;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('allowedCourses.title', 'Cours autorisés')}</CardTitle>
        <CardDescription>
          {t('allowedCourses.description', 'Sélectionnez les cours que ce client peut voir et réserver')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* All courses checkbox */}
        <div className="flex items-center space-x-2 p-3 border rounded-lg bg-muted/30">
          <Checkbox
            id="all-courses"
            checked={isAllCoursesEnabled}
            onCheckedChange={handleAllCoursesChange}
          />
          <Label 
            htmlFor="all-courses" 
            className="font-semibold cursor-pointer"
          >
            {t('allowedCourses.allCourses', 'Tous les cours')}
          </Label>
        </div>

        {/* Individual courses */}
        {!isAllCoursesEnabled && (
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">
              {t('allowedCourses.selectSpecific', 'Ou sélectionner des cours spécifiques :')}
            </Label>
            <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-3">
              {courses.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {t('allowedCourses.noCourses', 'Aucun cours disponible')}
                </p>
              ) : (
                courses.map(course => (
                  <div key={course.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`course-${course.id}`}
                      checked={selectedCourses.includes(course.id)}
                      onCheckedChange={(checked) => handleCourseToggle(course.id, checked as boolean)}
                    />
                    <Label 
                      htmlFor={`course-${course.id}`}
                      className="cursor-pointer font-normal"
                    >
                      {course.name}
                    </Label>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Info message */}
        {!isAllCoursesEnabled && selectedCourses.length > 0 && (
          <p className="text-sm text-muted-foreground">
            {t('allowedCourses.restrictionInfo', 'Ce client ne pourra voir que les cours sélectionnés dans son planning')}
          </p>
        )}

        {/* Save button */}
        <Button 
          onClick={handleSave}
          disabled={!hasChanges() || isSaving}
          className="w-full"
        >
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('common:saving', 'Enregistrement...')}
            </>
          ) : (
            t('common:save', 'Enregistrer')
          )}
        </Button>
      </CardContent>
    </Card>
  );
}